import React, { useRef, useState } from "react";
import { Plus, AlertTriangle, Edit2 } from "lucide-react";
import DockDoorSettings from "./DockDoorSettings";
import EditDockModal from "../../Modal/EditDockModalMain";
import notify from "../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import {
  updateDock,
  deleteDock,
  applyWarehouseSettingsToDocks,
  createCalendarEvents,
} from "../api/AppointmentsApi";
import { planWarehouseRecurringRollout } from "../utils/warehouseRecurringEvents";
import { isUnsavedRecurringEvent } from "../utils/recurringEventUtils";

export default function WarehouseDockManagement({
  selectedWarehouse,
  selectedLocationId,
  selectedWarehouseIndex,
  locations,
  setLocations,
  activeTarget,
  setActiveTarget,
  allDocksSelected,
  openAddDockModal,
  setOpenAddDockModal,
  onRefreshWarehouse = () => {},
}) {
  const getLocKey = (loc) => loc?.uid ?? loc?.id ?? loc?.locationRef;
  const [dockToEdit, setDockToEdit] = useState(null);
  const [isSavingDock, setIsSavingDock] = useState(false);
  const [isDeletingDock, setIsDeletingDock] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [viewRefreshToken, setViewRefreshToken] = useState(0);
  // Kept here, not in DockDoorSettings: switching docks remounts that panel, which
  // would reset its busy flag mid-request and allow a second apply.
  const [isApplyingRecurring, setIsApplyingRecurring] = useState(false);
  const applyingRecurringRef = useRef(false);
  const [copyOptions, setCopyOptions] = useState({
    hours: true,
    holidays: true,
    blackouts: true,
    recurringEvents: true,
    duration: true,
  });
  const warehouseSettings = selectedWarehouse?.settings || {};
  const warehouseHours =
    warehouseSettings?.regularHours && typeof warehouseSettings.regularHours === "object"
      ? Object.entries(warehouseSettings.regularHours)
      : [];
  const warehouseHolidays = warehouseSettings?.holidays || [];
  const warehouseBlackouts = warehouseSettings?.blackouts || [];
  const hasRecurringDrafts = (warehouseSettings?.recurringEvents || []).some(isUnsavedRecurringEvent);
  const formatHours = (from, to) => {
    const clean = (t) => (t && t.length >= 5 ? t.slice(0, 5) : t || "--:--");
    return `${clean(from)} - ${clean(to)}`;
  };

  const formatDurationToHours = (durationInMinutes = 120) => {
    return (durationInMinutes / 60)
      .toFixed(2)
      .replace(/\.00$/, "");
  };

  if (!selectedWarehouse) return null;

  // ---------------- MERGE DOCKS FOR PREVIEW ---------------- //
  const combinedDocks = [
    ...selectedWarehouse.docks,
    ...(selectedWarehouse.additionalDocks ?? []).map((d, i) => ({
      ...d,
      isNew: true,
      tempId: `new-${i}`, // safe fallback key
    })),
  ];

  const handleOpenEditDock = (dock, dockIdx) => {
    if (dock?.isNew) return;
    setDockToEdit({ ...dock, idx: dockIdx });
  };

  // Get selected docks with their calendar references
  const selectedDocks = combinedDocks
    .filter(d => d.selected && !d.isNew)
    .map(d => ({
      dockId: d.dockId ?? d.id,
      calendarRefId: d.calendarRefId
    }))
    .filter(d => d.dockId);
  
  const selectedDockIds = selectedDocks.map(d => d.dockId);
  const selectedDockIdsSet = new Set(selectedDockIds.map(String));

  const handleApplyToSelected = async () => {
    const sourceCalendarRef =
      selectedWarehouse?.calendar?.calendarRefId ||
      selectedWarehouse?.calendarRefId ||
      selectedWarehouse?.settings?.calendarRefId;

    if (!sourceCalendarRef) {
      notify.error({
        title: "Missing calendar",
        message: "Warehouse calendar reference is missing.",
      });
      return;
    }

    if (!selectedDocks.length) {
      notify.error({
        title: "No docks selected",
        message: "Select at least one existing dock to apply settings.",
      });
      return;
    }

    const payload = {
      sourceCalendarRefId: sourceCalendarRef,
      dockIds: selectedDockIds,
      copyHours: copyOptions.hours,
      copyHolidays: copyOptions.holidays,
      copyBlackouts: copyOptions.blackouts,
      copyRecurringEvents: copyOptions.recurringEvents,
      copyDuration: copyOptions.duration,
    };

    setIsApplying(true);
    try {
            await applyWarehouseSettingsToDocks(payload);
            notify.success({
        title: "Settings applied",
        message: "Warehouse settings were applied to selected docks.",
      });

      // Optimistically mirror the copied warehouse settings into selected docks
      setLocations((prev) => {
        const next = structuredClone(prev);
        const loc = next.find((l) => getLocKey(l) === selectedLocationId);
        const wh = loc?.warehouses?.[selectedWarehouseIndex];
        if (!wh?.docks?.length) return prev;

        wh.docks = wh.docks.map((d) => {
          const id = d.dockId ?? d.id;
          if (selectedDockIdsSet.has(String(id))) {
            return {
              ...d,
              settings: {
                ...warehouseSettings,
              },
            };
          }
          return d;
        });

                return next;
      });

      setShowApplyModal(false);
      await onRefreshWarehouse();
      // force a repaint of DockDoorSettings with the refreshed data
      setViewRefreshToken((t) => t + 1);
      setActiveTarget({ type: "warehouse" });
    } catch (error) {
      console.error("Failed to apply settings to docks:", error);
      notify.error({
        title: "Apply failed",
        message: "Could not apply settings. Please try again.",
      });
    } finally {
      setIsApplying(false);
    }
  };

  const warehouseLabel = selectedWarehouse?.locationName || "the warehouse";
  const warehouseDockCount = selectedWarehouse.docks?.length ?? 0;
  const dockScope =
    warehouseDockCount === 0
      ? ""
      : warehouseDockCount === 1
        ? " and its dock"
        : ` and all ${warehouseDockCount} of its docks`;

  // Recurring events added in the warehouse panel are written to the warehouse calendar
  // and every dock calendar in one request. Booking reads only a dock's own calendar,
  // so an event saved only on the warehouse would never block a dock.
  const handleApplyRecurringDraftsToAllDocks = async (drafts) => {
    if (applyingRecurringRef.current) return false;

    // Refreshing below would drop unsaved docks, and the new-dock save ignores
    // recurring events, so they would never get these events.
    if (selectedWarehouse.additionalDocks?.length) {
      notify.error({
        title: "Save new docks first",
        message:
          "New docks don't get recurring events until they are saved. Click Save at the top, then add this recurring event again.",
      });
      return false;
    }

    const { payload, problems } = planWarehouseRecurringRollout({
      warehouseName: selectedWarehouse?.locationName,
      warehouseSettings,
      docks: selectedWarehouse.docks ?? [],
      docksLoadFailed: Boolean(selectedWarehouse.docksLoadFailed),
      drafts,
    });

    if (problems.length) {
      const shown = problems.slice(0, 3).join(" ");
      notify.error({
        title: "Recurring events not applied",
        message: problems.length > 3 ? `${shown} (+${problems.length - 3} more)` : shown,
      });
      return false;
    }

    applyingRecurringRef.current = true;
    setIsApplyingRecurring(true);
    try {
      try {
        await createCalendarEvents(payload);
      } catch (error) {
        console.error("Failed to add warehouse recurring events:", error);
        // All the rows go through a single saveAll, so a failure here saves none of them.
        notify.error({
          title: "Recurring events not added",
          message: `Nothing was saved. ${error?.response?.data?.content || "Please try again."}`,
        });
        return false;
      }

      // Drop the drafts now rather than waiting for the refresh, which swallows its
      // own errors. Otherwise a failed refresh would leave them looking unsaved, and
      // a second apply would duplicate them on every calendar.
      setLocations((prev) => {
        const next = structuredClone(prev);
        const wh = next.find((l) => getLocKey(l) === selectedLocationId)?.warehouses?.[selectedWarehouseIndex];
        if (!wh?.settings?.recurringEvents) return prev;
        wh.settings = {
          ...wh.settings,
          recurringEvents: wh.settings.recurringEvents.filter((re) => !isUnsavedRecurringEvent(re)),
        };
        return next;
      });

      notify.success({
        title: "Recurring events added",
        message: `Added ${drafts.length} recurring event${drafts.length === 1 ? "" : "s"} to ${warehouseLabel}${dockScope}.`,
      });

      await onRefreshWarehouse();
      setViewRefreshToken((t) => t + 1);
      return true;
    } finally {
      applyingRecurringRef.current = false;
      setIsApplyingRecurring(false);
    }
  };

  const handleSaveDock = async ({ dockName: newName, settings: newSettings }) => {
    if (!dockToEdit) return;
    const dockId = dockToEdit.dockId ?? dockToEdit.id;
    const trimmedName = (newName || "").trim();

    if (!dockId || !trimmedName) return;

    const payload = {
      dockId,
      dockName: trimmedName,
    };

    const locationRefId =
      dockToEdit.locationRefId ||
      selectedWarehouse?.locationRef ||
      selectedWarehouse?.id ||
      selectedWarehouse?.uid;
    const calendarRefId =
      dockToEdit.calendarRefId ||
      selectedWarehouse?.calendar?.calendarRefId ||
      dockToEdit.calendarId;

    if (locationRefId) payload.locationRefId = locationRefId;
    if (calendarRefId) payload.calendarRefId = calendarRefId;

    setIsSavingDock(true);
    try {
      await updateDock(dockId, payload);

      setLocations((prev) => {
        const next = structuredClone(prev);
        const loc = next.find((l) => getLocKey(l) === selectedLocationId);
        const wh = loc?.warehouses?.[selectedWarehouseIndex];

        if (!wh?.docks?.length) return prev;

        const idx = wh.docks.findIndex(
          (d) => String(d.dockId ?? d.id) === String(dockId)
        );

        if (idx < 0) return prev;

        wh.docks[idx] = {
          ...wh.docks[idx],
          dockName: trimmedName,
          settings: newSettings || wh.docks[idx].settings,
        };
        return next;
      });

      notify.success({
        title: "Dock updated",
        message: `Saved "${trimmedName}" and applied the latest settings.`,
      });

      setDockToEdit(null);
    } catch (error) {
      console.error("Failed to update dock:", error);
      notify.error({
        title: "Update failed",
        message: "Could not update dock. Please try again.",
      });
    } finally {
      setIsSavingDock(false);
    }
  };

  const handleDeleteDock = async () => {
    if (!dockToEdit) return;
    const dockId = dockToEdit.dockId ?? dockToEdit.id;
    if (!dockId) return;

    setIsDeletingDock(true);
    try {
      await deleteDock(dockId);

      setLocations((prev) => {
        const next = structuredClone(prev);
        const loc = next.find((l) => getLocKey(l) === selectedLocationId);
        const wh = loc?.warehouses?.[selectedWarehouseIndex];

        if (!wh?.docks?.length) return prev;
        const originalLength = wh.docks.length;
        wh.docks = wh.docks.filter(
          (d) => String(d.dockId ?? d.id) !== String(dockId)
        );

        return wh.docks.length === originalLength ? prev : next;
      });

      setDockToEdit(null);
      setActiveTarget({ type: "warehouse" });

      notify.success({
        title: "Dock deleted",
        message: "The dock was deleted successfully.",
      });
    } catch (error) {
      console.error("Failed to delete dock:", error);
      notify.error({
        title: "Delete failed",
        message: "Could not delete dock. Please try again.",
      });
    } finally {
      setIsDeletingDock(false);
    }
  };

  return (
    <>
      {/* ----------------------------------------------------- */}
      {/* DOCK LIST */}
      {/* ----------------------------------------------------- */}
      <div className="mt-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Warehouse Dock Management</h3>
          <button
            type="button"
            onClick={() => {
              // That flow copies only what the warehouse calendar already has saved, so
              // it would report success while quietly dropping the new rows.
              if (hasRecurringDrafts) {
                notify.error({
                  title: "Unsaved recurring events",
                  message: "Apply or discard the new recurring events before applying settings to docks.",
                });
                return;
              }
              setShowApplyModal(true);
            }}
            disabled={!selectedDocks.length}
            className={`px-3 py-2 rounded-md text-sm font-semibold transition ${
              selectedDocks.length
                ? "bg-[#1e2a74] text-white hover:bg-[#18225c]"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            Apply to selected docks
          </button>
        </div>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,170px))] justify-start gap-fluid-3">

          {/* ---------------- ALL DOCKS TILE ---------------- */}
          <div
            className="p-fluid-2 rounded shadow-sm border border-gray-300 bg-[#e9eafc] min-h-[100px] cursor-pointer"
            onClick={() => {
              const selectAll = !combinedDocks.every((d) => !!d.selected);

              setLocations((prev) => {
                const next = structuredClone(prev);
                const loc = next.find((l) => getLocKey(l) === selectedLocationId);
                const wh = loc.warehouses[selectedWarehouseIndex];

                // update original docks
                wh.docks = wh.docks.map((d) => ({ ...d, selected: selectAll }));

                // update new docks
                if (wh.additionalDocks) {
                  wh.additionalDocks = wh.additionalDocks.map((d) => ({
                    ...d,
                    selected: selectAll,
                  }));
                }

                return next;
              });

              setActiveTarget({ type: "warehouse" });
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                className="accent-[#1e2a74]"
                checked={combinedDocks.length > 0 && combinedDocks.every((d) => !!d.selected)}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setLocations((prev) => {
                    const next = structuredClone(prev);
                    const loc = next.find((l) => getLocKey(l) === selectedLocationId);
                    const wh = loc.warehouses[selectedWarehouseIndex];

                    wh.docks = wh.docks.map((d) => ({ ...d, selected: checked }));
                    if (wh.additionalDocks) {
                      wh.additionalDocks = wh.additionalDocks.map((d) => ({
                        ...d,
                        selected: checked,
                      }));
                    }

                    return next;
                  });

                  setActiveTarget({ type: "warehouse" });
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <p className="text-[#1e2a74] text-fluid-base font-semibold leading-tight">
                Select all docks
              </p>
            </div>
          </div>

          {/* ---------------- INDIVIDUAL DOCK CARDS ---------------- */}
          {combinedDocks.map((dock, dockIdx) => {
            const selected = !!dock.selected;
            const isActive =
              activeTarget.type === "dock" && activeTarget.idx === dockIdx;

            return (
              <div
                key={dock.dockId || dock.tempId || dockIdx}
                onClick={() => {
                  setActiveTarget({ type: "dock", idx: dockIdx });

                  setLocations((prev) => {
                    const next = structuredClone(prev);
                    const loc = next.find((l) => getLocKey(l) === selectedLocationId);
                    const wh = loc.warehouses[selectedWarehouseIndex];

                    // update original docks
                    wh.docks = wh.docks.map((d, i) => ({
                      ...d,
                      selected: i === dockIdx && !dock.isNew,
                    }));

                    // update new docks
                    if (wh.additionalDocks?.length) {
                      wh.additionalDocks = wh.additionalDocks.map((d, i) => ({
                        ...d,
                        selected:
                          dock.isNew &&
                          i === dockIdx - wh.docks.length, // map correct new index
                      }));
                    }

                    return next;
                  });
                }}
                className={[
                    "p-fluid-1 rounded shadow-sm transition min-h-[90px] flex flex-col justify-between cursor-pointer",
                    dock.isNew
                      ? "border border-green-400 border-dashed bg-green-50 shadow-sm"
                      : "border border-gray-300 bg-white",
                  ].join(" ")}
               >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="accent-[#1e2a74]"
                      checked={selected}
                      onChange={(e) => {
                        const isChecked = e.target.checked;

                          setLocations((prev) => {
                            const next = structuredClone(prev);
                            const loc = next.find((l) => getLocKey(l) === selectedLocationId);
                            const wh = loc.warehouses[selectedWarehouseIndex];

                          // original docks
                          wh.docks = wh.docks.map((d, i) => ({
                            ...d,
                            selected: !dock.isNew && i === dockIdx ? isChecked : false,
                          }));

                          // new docks
                          if (wh.additionalDocks?.length) {
                            wh.additionalDocks = wh.additionalDocks.map((d, i) => ({
                              ...d,
                              selected:
                                dock.isNew &&
                                i === dockIdx - wh.docks.length
                                  ? isChecked
                                  : false,
                            }));
                          }

                          return next;
                        });

                        if (isChecked) {
                          setActiveTarget({ type: "dock", idx: dockIdx });
                        } else {
                          setActiveTarget({ type: "warehouse" });
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />

                    <p className="text-[#1e2a74] font-semibold">
                      {dock.dockName || `Dock ${dockIdx + 1}`}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {dock.isNew && (
                      <span className="text-xs font-bold text-green-700 bg-green-200 px-2 py-0.5 rounded">
                        NEW
                      </span>
                    )}

                    {!dock.isNew && (
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDock(dock, dockIdx);
                        }}
                        aria-label="Edit dock"
                      >
                        <Edit2 className="w-4 h-4 text-gray-600" />
                      </button>
                    )}
                  </div>
                </div>
                {dock.isNew && (
                  <div
                    className="
                      mt-fluid-1 
                      flex items-start gap-fluid-2 
                      p-fluid-2 
                      rounded-md 
                      bg-red-50 
                      border border-red-200 
                      shadow-[0_1px_2px_rgba(0,0,0,0.04)] 
                    "
                  >
                    <AlertTriangle
                      className="w-fluid-icon-xl h-fluid-icon-xs text-red-600 mt-0.5"
                      strokeWidth={2.2}
                    />

                    <p className="text-fluid-xs text-red-700 font-semibold leading-snug">
                      This dock will be created only after saving.
                    </p>
                  </div>
                )}
                {!dock.isNew && (
                  null
                )}
              </div>
            );
          })}

          {/* ---------------- ADD DOCK BUTTON ---------------- */}
          <button
            type="button"
            onClick={() => setOpenAddDockModal(true)}
            className="group flex flex-col items-center justify-center gap-2 p-fluid-2 rounded-lg border border-dashed border-[#1e2a74] bg-white min-h-[100px] w-full hover:border-[#16205c] hover:bg-[#f8f9ff] transition-colors duration-200"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#e9eafc] group-hover:bg-[#d7d9f8] transition-colors">
              <Plus className="w-4 h-4 text-[#1e2a74]" strokeWidth={2.5} />
            </div>
            <span className="text-fluid-sm font-medium text-[#1e2a74] group-hover:text-[#16205c] transition-colors">
              Add Dock
            </span>
          </button>
        </div>
      </div>

      {/* ----------------------------------------------------- */}
      {/* TIMESHEET SECTION */}
      {/* ----------------------------------------------------- */}
      <h3 className="text-lg font-semibold mt-6 mb-2">
        {allDocksSelected ? (
          <>
            Selected {selectedWarehouse?.locationName} configuration:{" "}
            <span className="text-[#1e2a74]">for all docks</span>
          </>
        ) : activeTarget.type === "warehouse" ? (
          <>
            {selectedWarehouse?.locationName}{" "}
            <span className="text-[#1e2a74]">configuration</span>
          </>
        ) : (
          <>
            Selected Dock:{" "}
            <span className="text-[#1e2a74]">
              {combinedDocks[activeTarget.idx]?.dockName}
            </span>
          </>
        )}
      </h3>

      <div className="bg-white border border-gray-300 rounded-md p-fluid-3 mb-fluid-3">
        <DockDoorSettings
          key={
            activeTarget.type === "warehouse"
              ? `timesheet-warehouse-${selectedWarehouseIndex}-${viewRefreshToken}`
              : `timesheet-dock-${selectedWarehouseIndex}-${activeTarget.idx}-${viewRefreshToken}`
          }
          settings={
            activeTarget.type === "warehouse"
              ? selectedWarehouse.settings
              : combinedDocks[activeTarget.idx]?.settings ?? {}
          }
          onChange={(newSettings) => {
            const updated = structuredClone(locations);
            const loc = updated.find((l) => getLocKey(l) === selectedLocationId);
            const wh = loc.warehouses[selectedWarehouseIndex];

            const isNew = combinedDocks[activeTarget.idx]?.isNew;

            if (activeTarget.type === "warehouse") {
              wh.settings = newSettings;
            } else if (!isNew) {
              wh.docks[activeTarget.idx].settings = newSettings;
            } else {
              const addIdx = activeTarget.idx - wh.docks.length;
              wh.additionalDocks[addIdx].settings = newSettings;
            }

            setLocations(updated);
          }}
          isViewOnly={true}
          isWarehouseView={activeTarget.type === "warehouse"}
          canAddRecurringDrafts={activeTarget.type === "warehouse"}
          onApplyRecurringDrafts={handleApplyRecurringDraftsToAllDocks}
          recurringDraftsBusy={isApplyingRecurring}
          recurringDraftsApplyLabel={warehouseDockCount ? "Apply to all docks" : "Save"}
          recurringDraftsHint={`New recurring events are saved to ${warehouseLabel}${dockScope}.`}
           /** 🔥 VERY IMPORTANT — Add this */
          isNewDock={activeTarget.type === "dock" && combinedDocks[activeTarget.idx]?.isNew}
        />
      </div>

      {dockToEdit && (
        <EditDockModal
          dock={dockToEdit}
          onClose={() => setDockToEdit(null)}
          onSave={handleSaveDock}
          onDelete={handleDeleteDock}
          isSaving={isSavingDock}
          isDeleting={isDeletingDock}
          onSaveComplete={async () => {
            await onRefreshWarehouse();
            setViewRefreshToken((t) => t + 1);
            setDockToEdit(null);
          }}
        />
      )}

      {showApplyModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl p-8 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-[#1e2a74]">
                  Apply {selectedWarehouse?.locationName || "warehouse"} settings
                </h3>
                <p className="text-sm text-gray-600">
                  Push selected sections from the warehouse calendar to {selectedDocks.length} dock(s).
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowApplyModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="border border-[#e2e6ff] bg-[#f8f9ff] rounded-lg p-4 text-sm space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-gray-700 font-semibold">Warehouse calendar</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[18%_32%_20%_30%] xl:grid-cols-[12%_33%_20%_35%] gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 uppercase">Duration</p>
                  <p className="text-sm font-semibold text-[#1e2a74]">
                    {formatDurationToHours(selectedWarehouse?.settings?.appointmentDuration ?? 120)} hour(s)
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 uppercase">Hours (weekly)</p>
                  <div className="text-sm text-gray-800">
                    {warehouseHours.length ? (
                      warehouseHours.slice(0, 7).map(([day, slot]) => (
                        <div key={day} className="flex items-center gap-3">
                          <span className="min-w-[85px]">{day}</span>
                          <span className="text-xs text-gray-600">
                            {formatHours(slot.from, slot.to)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <span className="text-gray-500">No hours set</span>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 uppercase">Holidays</p>
                  <div className="text-sm text-gray-800">
                    {warehouseHolidays.length ? (
                      <>
                        <span>{warehouseHolidays.length} date(s)</span>
                        <div className="text-xs text-gray-500 space-y-1">
                          {warehouseHolidays.slice(0, 4).map((h, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span>{h.date}</span>
                              {h.reason && <span className="text-[11px] text-gray-600 truncate max-w-[140px]">{h.reason}</span>}
                            </div>
                          ))}
                          {warehouseHolidays.length > 4 && (
                            <div>+{warehouseHolidays.length - 4} more</div>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-500">None</span>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 uppercase">Blackouts</p>
                  <div className="text-sm text-gray-800">
                    {warehouseBlackouts.length ? (
                      <>
                        <span>{warehouseBlackouts.length} exception(s)</span>
                        <div className="text-xs text-gray-500 space-y-1">
                          {warehouseBlackouts.slice(0, 4).map((b, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span>
                                {b.from} {b.to && b.to !== b.from ? `→ ${b.to}` : ""}
                              </span>
                              {b.reason && <span className="text-[11px] text-gray-600 truncate max-w-[140px]">{b.reason}</span>}
                            </div>
                          ))}
                          {warehouseBlackouts.length > 4 && (
                            <div>+{warehouseBlackouts.length - 4} more</div>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-500">None</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {[
                { key: "hours", label: "Hours (weekly open slots)" },
                { key: "holidays", label: "Holidays (yearly closed days)" },
                { key: "blackouts", label: "Blackout days (exceptions)" },
                { key: "recurringEvents", label: "Recurring events" },
                { key: "duration", label: "Appointment duration" },
              ].map(opt => (
                <label key={opt.key} className="flex items-center gap-2 text-sm rounded-md px-3 py-2">
                  <input
                    type="checkbox"
                    checked={copyOptions[opt.key]}
                    onChange={e =>
                      setCopyOptions(prev => ({ ...prev, [opt.key]: e.target.checked }))
                    }
                    className="accent-[#1e2a74]"
                  />
                  <span className="text-gray-800">{opt.label}</span>
                </label>
              ))}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowApplyModal(false)}
                className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-100"
                disabled={isApplying}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyToSelected}
                className={`px-4 py-2 rounded-md text-sm font-semibold transition ${
                  isApplying
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-[#1e2a74] text-white hover:bg-[#18225c]"
                }`}
                disabled={isApplying}
              >
                {isApplying ? "Applying..." : "Apply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
